import {Paper} from '@material-ui/core';
import {toEither} from 'Core/models/Async';
import {EmptyString, NonEmptyString} from 'Core/models/EmptyString';
import {sequenceT} from 'fp-ts/lib/Apply';
import {either, fold as foldEither} from 'fp-ts/lib/Either';
import {fold} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useEffect} from 'react';
import {useDispatch, useSelector, connect} from 'react-redux';
import {ReducerState} from 'rootReducer';
import Loading from 'Shared/components/Loading';
import {getNameFromClaims} from 'Shared/components/nameFormatter';
import {renderAsync, renderAsyncView} from 'Shared/components/RenderAsync';
import withUserClaims, {WithClaimsProps} from 'Shared/components/withUserClaims';
import {getIsUserEmployee} from 'Shared/utilities/Auth';
import EmployeeUserInformationSection from 'Users/components/UserProfile/EmployeeUserInformationSection';
import UserEmailSection from 'Users/components/UserProfile/UserEmailSection';
import UserInformationSection from 'Users/components/UserProfile/UserInformationSection';
import UserMobilePhoneSection from 'Users/components/UserProfile/UserMobilePhoneSection';
import UserSecurityQuestionsSection from 'Users/components/UserProfile/UserSecurityQuestionsSection';
import * as UserAccountActions from 'Users/ducks/userAccount';
import {NameInformation, UnchangedNameInformation} from 'Users/models/NameInformation';
import PassResetSection from './PasswordResetSection';
import {updatefeatureVariables} from 'Core/ducks/featureToggle';
import './UserProfile.sass';
import {changeAnalyticsState} from 'Core/ducks/analytics';
import {ANALYTICS_EVENT_ACTION} from 'Core/components/Analytics/AnalyticsEventConstants';

type UserProfileProps = ReturnType<typeof mapStateToProps> & WithClaimsProps & typeof UserAccountActions;

export const LoadingTile = () => (
  <Paper className="user-profile__user-info-section">
    <Loading />
  </Paper>
);

export const UserProfile = (props: UserProfileProps) => {
  const {
    changeEmail,
    changeMobilePhone,
    changeNameInformation,
    changeSecurityQuestionForm,
    claims,
    currentNameInformation,
    email,
    getSecurityQuestions,
    getUserProfile,
    mobilePhone,
    nameInformation,
    profile,
    saveEmail,
    saveMobilePhone,
    saveNameInformation,
    saveSecurityQuestions,
    securityQuestions,
    securityQuestionForm,
  } = props;
  const {id} = claims;

  const {featureVariables} = useSelector((state: ReducerState) => state.featureToggle);
  const dispatch = useDispatch();

  const defaultNameInformation = pipe(
    currentNameInformation,
    fold(
      () => new UnchangedNameInformation(new EmptyString(), new EmptyString()),
      names =>
        new UnchangedNameInformation(NonEmptyString.create(names.firstName), NonEmptyString.create(names.lastName))
    )
  );

  useEffect(() => {
    dispatch(updatefeatureVariables({...featureVariables, FOOTER_OPEN_DEFAULT: true}));
  }, []);

  useEffect(() => {
    getUserProfile(id);
    getSecurityQuestions();
  }, [id, getSecurityQuestions, getUserProfile]);

  const {analytics} = useSelector((state: ReducerState) => state.analytics);

  const handleChangeNameInformation = (nameInformation: NameInformation) => {
    changeNameInformation(nameInformation);
    dispatch(
      changeAnalyticsState({
        page: analytics.page,
        ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.GLOBAL_USER_PROFILE_DROPDOWN},
      })
    );
  };
  const securityQuestionData = sequenceT(either)(toEither(securityQuestions), toEither(securityQuestionForm));
  const IAUserInformationSection = (
    <div>
      <UserInformationSection
        userId={id}
        saveNameInformation={saveNameInformation}
        currentNameInformation={defaultNameInformation}
        nameInformation={nameInformation}
        onChange={nameInformation => handleChangeNameInformation(nameInformation)}
      />
      {renderAsyncView(
        email,
        loadedEmail => {
          return <UserEmailSection onChange={changeEmail} userId={id} asyncEmail={loadedEmail} saveEmail={saveEmail} />;
        },
        LoadingTile
      )}
      {renderAsyncView(
        mobilePhone,
        loadedPhone => {
          return (
            <UserMobilePhoneSection
              onChange={changeMobilePhone}
              userId={id}
              phoneEither={loadedPhone}
              saveMobilePhone={saveMobilePhone}
            />
          );
        },
        LoadingTile
      )}
      {pipe(
        securityQuestionData,
        foldEither(
          error => renderAsync(error, LoadingTile),
          ([securityQuestions, securityQuestionForm]) => {
            return (
              <UserSecurityQuestionsSection
                onChange={changeSecurityQuestionForm}
                userId={id}
                saveSecurityQuestions={saveSecurityQuestions}
                securityQuestionForm={securityQuestionForm}
                securityQuestions={securityQuestions}
              />
            );
          }
        )
      )}
      <PassResetSection />
    </div>
  );

  return (
    <div className="user-profile">
      {pipe(
        getIsUserEmployee(),
        fold(
          () => IAUserInformationSection,
          isEmployee =>
            isEmployee
              ? renderAsyncView(profile, loadedProfile => (
                  <EmployeeUserInformationSection
                    profile={loadedProfile}
                    showPosition={loadedProfile.position !== 'None'}
                  />
                ))
              : IAUserInformationSection
        )
      )}
    </div>
  );
};

const mapStateToProps = (state: ReducerState) => ({
  nameInformation: state.user.userAccount.nameInformation,
  currentNameInformation: getNameFromClaims(state.user.profile.userClaims),
  profile: state.user.userAccount.userProfile,
  email: state.user.userAccount.email,
  mobilePhone: state.user.userAccount.mobilePhone,
  securityQuestions: state.user.userAccount.questions,
  securityQuestionForm: state.user.userAccount.securityQuestionForm,
});

export default withUserClaims(
  connect(mapStateToProps, {
    ...UserAccountActions,
  })(UserProfile)
);
